import { QueryHookOptions, useQuery } from "@apollo/client";
import dayjs from "dayjs";

import {
  AccountType,
  BeneficiaryType,
  DateFormat,
  GoalType,
  SuggestedAccountTypeQuery,
  SuggestedAccountTypeQueryVariables,
  SuggestedAccountWho,
} from "@ctypes";
import { SUGGESTED_ACCOUNT_TYPE } from "@graphql";
import { useGoalFlow } from "./useGoalFlow";

/**
 * Ported from mobile useSuggestedAccountType: asks the backend for an
 * account-type recommendation based on who the goal is for, the goal type,
 * the tax-flexibility preference and the beneficiary's age.
 */

type SupportedAccountType =
  | AccountType.JuniorRothIra
  | AccountType.CustodialRothIra
  | AccountType.FiveTwoNinePlan
  | AccountType.GeneralInvesting
  | AccountType.RothIra
  | AccountType.TraditionalIra
  | AccountType.HarvestUtmaUgma
  | AccountType.Reserve;

const SUPPORTED_ACCOUNT_TYPES = new Set<string>([
  AccountType.JuniorRothIra,
  AccountType.CustodialRothIra,
  AccountType.FiveTwoNinePlan,
  AccountType.GeneralInvesting,
  AccountType.RothIra,
  AccountType.TraditionalIra,
  AccountType.HarvestUtmaUgma,
  AccountType.Reserve,
]);

const BENEFICIARY_TYPE_TO_WHO: Record<BeneficiaryType, SuggestedAccountWho> = {
  [BeneficiaryType.Child]: SuggestedAccountWho.Child,
  [BeneficiaryType.Self]: SuggestedAccountWho.Myself,
  [BeneficiaryType.Adult]: SuggestedAccountWho.Together,
};

const isAdultBeneficiary = (beneficiaryType: BeneficiaryType) =>
  [BeneficiaryType.Adult, BeneficiaryType.Self].includes(beneficiaryType);

const getDefaultAccountTypes = (
  beneficiaryType: BeneficiaryType,
): {
  recommendedAccountType: SupportedAccountType;
  alternativeAccountTypes: SupportedAccountType[];
} => ({
  recommendedAccountType: AccountType.GeneralInvesting,
  alternativeAccountTypes: isAdultBeneficiary(beneficiaryType)
    ? [AccountType.RothIra, AccountType.TraditionalIra]
    : [],
});

type UseSuggestedAccountTypeOptions = Omit<
  QueryHookOptions<SuggestedAccountTypeQuery, SuggestedAccountTypeQueryVariables>,
  "variables"
> & { isSharedGoal?: boolean };

export const useSuggestedAccountType = ({
  isSharedGoal,
  ...options
}: UseSuggestedAccountTypeOptions = {}) => {
  const { goalProps } = useGoalFlow();

  const beneficiaryType = goalProps.beneficiaryType ?? BeneficiaryType.Child;
  const goalType = goalProps.goalType ?? GoalType.Education;

  const beneficiaryDateOfBirth = goalProps.dateOfBirth
    ? dayjs(goalProps.dateOfBirth).format(DateFormat.User)
    : undefined;

  const { data, error, ...rest } = useQuery<
    SuggestedAccountTypeQuery,
    SuggestedAccountTypeQueryVariables
  >(SUGGESTED_ACCOUNT_TYPE, {
    ...options,
    variables: {
      input: {
        who: BENEFICIARY_TYPE_TO_WHO[beneficiaryType],
        goalType,
        flexibilityPreference: goalProps.flexibilityPreference,
        beneficiaryDateOfBirth,
        isSharedGoal,
      },
    },
  });

  const recommended = data?.suggestedAccountType?.recommended;
  const alternatives = data?.suggestedAccountType?.alternatives ?? [];

  const validRecommended =
    recommended && SUPPORTED_ACCOUNT_TYPES.has(recommended)
      ? (recommended as SupportedAccountType)
      : undefined;

  const validAlternatives = alternatives.filter((type): type is SupportedAccountType =>
    SUPPORTED_ACCOUNT_TYPES.has(type),
  );

  const shouldUseDefaultAccountTypes =
    Boolean(error) || (!validRecommended && validAlternatives.length === 0);

  const defaultAccountTypes = getDefaultAccountTypes(beneficiaryType);

  const recommendedAccountType: SupportedAccountType = shouldUseDefaultAccountTypes
    ? defaultAccountTypes.recommendedAccountType
    : (validRecommended ?? defaultAccountTypes.recommendedAccountType);

  const alternativeAccountTypes = shouldUseDefaultAccountTypes
    ? defaultAccountTypes.alternativeAccountTypes
    : validAlternatives;

  return {
    suggestedAccountType: data?.suggestedAccountType,
    recommendedAccountType,
    alternativeAccountTypes,
    ...rest,
  };
};
